import type { PaginationData } from "@/components/ui/pagination"
import type { AppPageProps } from "@/types"
import type { AssetType, AssetTypeInfo } from "@/lib/assetTypes"

export interface Asset {
  id: string
  code: string
  notes: string | null
  assetable_type: AssetType
  last_event_at: string | null
  stage: { id: string; name: string; color: string }
  customer: { id: string; name: string; street: string | null; phone: string | null } | null
  supplier: { id: string; name: string } | null
  location: { id: string; name: string; street: string | null; city: string | null; state: string | null } | null
  tags: Array<{ id: string; name: string; color: string }>
  qr_code_url?: string | null
  // Keg-specific
  serial_number?: string | null
  batch_number?: string | null
  expiration_date?: string | null
  tare?: number | null
  volume?: number | string | null
  bought_at?: string
  product?: { id: string; name: string } | null
  container?: { id: string; name: string } | null
  // Fridge-specific
  brand?: string | null
  model?: string | null
  capacity?: number | null
  // Cylinder-specific
  pressure_rating?: number | null
  last_inspection_date?: string | null
  expiry_date?: string | null
  // BeerTap-specific
  tap_count?: number | null
}

export interface AssetFormData {
  // Shared fields
  code: string
  stage_id: string
  customer_id: string
  supplier_id: string
  location_id: string
  tag_ids: string[]
  notes: string
  // Keg-specific
  product_id: string
  container_id: string
  bought_at: string
  serial_number: string
  batch_number: string
  expiration_date: string
  tare: string
  volume: string
  // Fridge / BeerTap
  brand: string
  model: string
  // Fridge
  capacity: string
  // Cylinder
  pressure_rating: string
  last_inspection_date: string
  expiry_date: string
  // BeerTap
  tap_count: string
}

export const emptyAssetFormData: AssetFormData = {
  code: "",
  stage_id: "",
  customer_id: "",
  supplier_id: "",
  location_id: "",
  tag_ids: [],
  notes: "",
  product_id: "",
  container_id: "",
  bought_at: new Date().toISOString().split("T")[0],
  serial_number: "",
  batch_number: "",
  expiration_date: "",
  tare: "",
  volume: "",
  brand: "",
  model: "",
  capacity: "",
  pressure_rating: "",
  last_inspection_date: "",
  expiry_date: "",
  tap_count: "",
}

export interface InventoryFilters {
  code: string
  product_id?: string
  container_id?: string
  volume?: string
  stage_id: string
  customer_id: string
  location_id: string
  tag_id: string
  sort: string
  direction: "asc" | "desc"
}

export interface SelectOption {
  id: string
  name: string
  color?: string
}

export interface ProductOption extends SelectOption {
  default_expiration_days?: number | null
}

export interface StageWithRules extends SelectOption {
  rules: string[]
  canMoveTo: boolean
}

export interface MoveStageFormData {
  stage_id: string
  product_id: string
  customer_id: string
  batch_number: string
  expiration_date: string
  tag_ids: string[]
  notes: string
}

export interface MoveLocationFormData {
  location_id: string
  notes: string
}

export interface AssetEvent {
  id: string
  event_type: "stage_change" | "location_change"
  notes: string | null
  metadata: Record<string, unknown>
  created_at: string
  user: { id: number; name: string | null; email: string } | null
  // Stage-change fields (present when event_type === "stage_change")
  from_stage?: { id: string; name: string; color: string } | null
  to_stage?: { id: string; name: string; color: string } | null
  product?: { id: string; name: string } | null
  customer?: { id: string; name: string } | null
  batch_number?: string | null
  // Location-change fields (present when event_type === "location_change")
  from_location?: { id: string; name: string } | null
  to_location?: { id: string; name: string } | null
}

export interface InventoryShowPageProps extends AppPageProps {
  asset: Asset
  events: AssetEvent[]
  assetType: AssetType
  assetTypes: AssetTypeInfo[]
  stages: StageWithRules[]
  customers: SelectOption[]
  suppliers: SelectOption[]
  locations: SelectOption[]
  tags: SelectOption[]
  products?: ProductOption[]
  containers?: SelectOption[]
  volumes?: SelectOption[]
}

export interface InventoryPageProps extends AppPageProps {
  assets: Asset[]
  pagination: PaginationData
  filters: InventoryFilters
  assetType: AssetType
  assetTypes: AssetTypeInfo[]
  stages: StageWithRules[]
  customers: SelectOption[]
  suppliers: SelectOption[]
  locations: SelectOption[]
  tags: SelectOption[]
  products?: ProductOption[]
  containers?: SelectOption[]
  volumes?: SelectOption[]
}

export interface InventoryEventsPageProps extends AppPageProps {
  asset: { id: string; code: string }
  assetType: AssetType
  events: AssetEvent[]
  pagination: PaginationData
}
